import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useFirestore } from "reactfire";
import { useFeedback } from "../../../providers/feedback/feedback";

const REDON_DESCRIPTION = `REDON is the official blood donors network of ICYM Udupi Diocese, dedicated to responding to emergency blood requirements for anyone in need. Through this initiative, the youth of the diocese come forward with the noble act of blood donation, offering timely support to those in critical situations.

Though REDON has been actively functioning for several years, it was officially named and inaugurated on 25th May 2025, marking a significant milestone in its mission of service.

The communication and coordination of REDON are managed by the ICYM Diocesan Joint Secretary / REDON In-charge, in collaboration with Deanery REDON In-charges and Unit In-charges. The initiative operates under the guidance and support of the Director and the ICYM Central Council.`;

type RedonPageSettings = {
  description: string;
  diocesanInchargeName: string;
  diocesanInchargeContact: string;
  updatedAt?: string;
};

const DEFAULT_SETTINGS: RedonPageSettings = {
  description: REDON_DESCRIPTION,
  diocesanInchargeName: "",
  diocesanInchargeContact: "",
};

export const REDON_PAGE_SETTINGS_DOC_ID = "content";
export const REDON_PAGE_SETTINGS_COLLECTION = "redonPageSettings";

export default function RedonPageSettingsTab() {
  const firestore = useFirestore();
  const { showSnackbar } = useFeedback();
  const [formValues, setFormValues] = useState<RedonPageSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadSettings() {
      try {
        const snapshot = await getDoc(
          doc(
            firestore,
            REDON_PAGE_SETTINGS_COLLECTION,
            REDON_PAGE_SETTINGS_DOC_ID,
          ),
        );

        if (!isMounted) {
          return;
        }

        if (snapshot.exists()) {
          setFormValues({
            ...DEFAULT_SETTINGS,
            ...(snapshot.data() as Partial<RedonPageSettings>),
          });
        }
      } catch (error) {
        console.error("Failed to load REDON page settings", error);
        showSnackbar("Failed to load REDON page settings");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [firestore, showSnackbar]);

  async function handleSave() {
    try {
      setIsSaving(true);
      await setDoc(
        doc(
          firestore,
          REDON_PAGE_SETTINGS_COLLECTION,
          REDON_PAGE_SETTINGS_DOC_ID,
        ),
        {
          ...formValues,
          description: formValues.description.trim(),
          diocesanInchargeName: formValues.diocesanInchargeName.trim(),
          diocesanInchargeContact: formValues.diocesanInchargeContact.trim(),
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );
      showSnackbar("REDON page settings saved successfully");
    } catch (error) {
      console.error("Failed to save REDON page settings", error);
      showSnackbar("Failed to save REDON page settings");
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography>Loading REDON page settings...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" fontWeight={600} mb={1}>
        Public REDON Page Content
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Update the description and diocesan in-charge details shown on the public REDON page.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            multiline
            minRows={10}
            value={formValues.description}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Diocesan In-charge Name"
            placeholder="Enter diocesan in-charge name"
            value={formValues.diocesanInchargeName}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                diocesanInchargeName: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Diocesan In-charge Contact"
            placeholder="Enter contact number if available"
            value={formValues.diocesanInchargeContact}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                diocesanInchargeContact: event.target.value,
              }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save REDON Page Content"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
